import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import ProfessionalProfileCard from './components/ProfessionalProfileCard';

import professionalBookingService from '../../services/professionalBookingService';
import { getApprovedProfessionals } from '../../services/professionalService';
import { useAuth } from '../../contexts/AuthContext';
import PostBookingReviewForm from './components/PostBookingReviewForm';

/**
 * Normalize a DB professional record (camelCase from toCamel) to the shape
 * expected by ProfessionalProfileCard and the booking flow.
 */
const normalizeDbProfessional = (p, serviceColor) => ({
  id: p?.id,
  name: p?.fullName || 'Professionnel',
  title: p?.specialty || p?.category || '',
  verified: p?.isVerified || false,
  color: serviceColor || 'from-amber-500 to-orange-500',
  rating: parseFloat(p?.ratingAverage) || 0,
  completedJobs: p?.completedJobs || 0,
  experience: p?.yearsExperience || 0,
  responseTime: p?.responseTimeHours ? `< ${p?.responseTimeHours}h` : 'Sur demande',
  priceRange: p?.hourlyRate ?
  `À partir de ${Number(p?.hourlyRate)?.toLocaleString('fr-FR')} FCFA/h` :
  p?.dailyRate ?
  `À partir de ${Number(p?.dailyRate)?.toLocaleString('fr-FR')} FCFA/j` :
  'Sur devis',
  tags: p?.tags || [],
  bio: p?.bio || '',
  calendarSeed: Math.abs(p?.id?.charCodeAt?.(0) || 0) % 10,
  stats: [
  { icon: 'Briefcase', value: `${p?.completedJobs || 0} missions`, label: 'Réalisées' },
  { icon: 'Award', value: `${parseFloat(p?.ratingAverage || 0)?.toFixed(1)}/5`, label: 'Note moyenne' },
  { icon: 'Clock', value: p?.responseTimeHours ? `< ${p?.responseTimeHours}h` : 'Sur demande', label: 'Temps de réponse' },
  { icon: 'MapPin', value: p?.commune || p?.city || 'Abidjan', label: 'Zone principale' }],

  portfolio: [],
  reviews: [],
  // Keep original DB fields for booking submission
  _db: p
});

const SERVICES = [
{
  id: 'architecte',
  label: 'Architecte',
  icon: 'PenTool',
  color: 'from-amber-500 to-orange-500',
  bg: 'bg-amber-50',
  border: 'border-amber-200',
  description: 'Plans, permis de construire, supervision de travaux',
  priceRange: 'Sur devis'
},
{
  id: 'geometre',
  label: 'Géomètre',
  icon: 'Ruler',
  color: 'from-blue-500 to-cyan-500',
  bg: 'bg-blue-50',
  border: 'border-blue-200',
  description: 'Bornage, relevé topographique, division parcellaire',
  priceRange: 'À partir de 150 000 FCFA'
},
{
  id: 'decorateur',
  label: 'Décorateur',
  icon: 'Palette',
  color: 'from-pink-500 to-rose-500',
  bg: 'bg-pink-50',
  border: 'border-pink-200',
  description: 'Aménagement intérieur, choix matériaux, mise en scène',
  priceRange: 'À partir de 80 000 FCFA'
},
{
  id: 'demenageur',
  label: 'Déménageur',
  icon: 'Truck',
  color: 'from-green-500 to-emerald-500',
  bg: 'bg-green-50',
  border: 'border-green-200',
  description: 'Transport de meubles, emballage, installation',
  priceRange: 'À partir de 50 000 FCFA'
},
{
  id: 'nettoyage',
  label: 'Nettoyage',
  icon: 'Sparkles',
  color: 'from-teal-500 to-cyan-500',
  bg: 'bg-teal-50',
  border: 'border-teal-200',
  description: 'Nettoyage fin de chantier, entretien régulier, désinfection',
  priceRange: 'À partir de 30 000 FCFA'
},
{
  id: 'electricien',
  label: 'Électricien',
  icon: 'Zap',
  color: 'from-yellow-500 to-amber-500',
  bg: 'bg-yellow-50',
  border: 'border-yellow-200',
  description: 'Installation électrique, tableau, domotique',
  priceRange: 'À partir de 40 000 FCFA'
},
{
  id: 'plombier',
  label: 'Plombier',
  icon: 'Droplets',
  color: 'from-sky-500 to-blue-500',
  bg: 'bg-sky-50',
  border: 'border-sky-200',
  description: 'Plomberie sanitaire, chauffe-eau, réparations',
  priceRange: 'À partir de 35 000 FCFA'
},
{
  id: 'menuisier',
  label: 'Menuisier',
  icon: 'Hammer',
  color: 'from-orange-500 to-red-500',
  bg: 'bg-orange-50',
  border: 'border-orange-200',
  description: 'Portes, fenêtres, placards sur mesure, parquet',
  priceRange: 'Sur devis'
},
{
  id: 'climatisation',
  label: 'Climatisation',
  icon: 'Wind',
  color: 'from-indigo-500 to-blue-500',
  bg: 'bg-indigo-50',
  border: 'border-indigo-200',
  description: 'Installation, entretien et réparation de climatiseurs',
  priceRange: 'À partir de 60 000 FCFA'
},
{
  id: 'securite',
  label: 'Sécurité',
  icon: 'ShieldCheck',
  color: 'from-slate-600 to-gray-700',
  bg: 'bg-slate-50',
  border: 'border-slate-200',
  description: 'Alarme, vidéosurveillance, contrôle d\'accès, gardiennage',
  priceRange: 'Sur devis'
}];


// Mock professionals data per service
const PROFESSIONALS_DATA = {
  architecte: [
  {
    id: 'arch-1', name: 'Kouamé Assi', title: 'Architecte DPLG', verified: true,
    color: 'from-amber-500 to-orange-500', rating: 4.8, completedJobs: 47, experience: 12,
    responseTime: '< 2h', priceRange: 'Sur devis',
    tags: ['Plans 3D', 'Permis de construire', 'Rénovation', 'Neuf'],
    bio: 'Architecte diplômé avec 12 ans d\'expérience en Côte d\'Ivoire. Spécialisé dans les villas modernes et les immeubles résidentiels à Abidjan.',
    calendarSeed: 3,
    stats: [
    { icon: 'Home', value: '47 projets', label: 'Réalisés' },
    { icon: 'Award', value: '4.8/5', label: 'Note moyenne' },
    { icon: 'Clock', value: '< 2h', label: 'Temps de réponse' },
    { icon: 'MapPin', value: 'Cocody', label: 'Zone principale' }],

    portfolio: [
    { icon: 'Home', title: 'Villa moderne à Cocody', description: 'Construction neuve 4 chambres, piscine, double garage', value: '85 000 000 FCFA', date: 'Mars 2026' },
    { icon: 'Building2', title: 'Immeuble R+3 Marcory', description: '12 appartements, parkings souterrains, espaces verts', value: '320 000 000 FCFA', date: 'Jan 2026' },
    { icon: 'PenTool', title: 'Rénovation villa Plateau', description: 'Réaménagement complet intérieur et façade', value: '45 000 000 FCFA', date: 'Nov 2025' }],

    reviews: [
    { author: 'Fatou Diallo', rating: 5, date: 'Avr 2026', text: 'Excellent architecte, très professionnel et à l\'écoute. Les plans étaient parfaits et les délais respectés. Je recommande vivement ses services pour tout projet de construction.' },
    { author: 'Yves Kouassi', rating: 5, date: 'Fév 2026', text: 'Travail impeccable, très créatif et respectueux des contraintes budgétaires.' },
    { author: 'Marie Bamba', rating: 4, date: 'Jan 2026', text: 'Bon professionnel, quelques ajustements nécessaires mais résultat final très satisfaisant.' },
    { author: 'Alain Traoré', rating: 5, date: 'Déc 2025', text: 'Kouamé a su transformer notre vision en réalité. Superbe villa, merci !' }]

  },
  {
    id: 'arch-2', name: 'Ama Sika Mensah', title: 'Architecte & Designer', verified: true,
    color: 'from-rose-500 to-pink-600', rating: 4.6, completedJobs: 31, experience: 8,
    responseTime: '< 4h', priceRange: 'Sur devis',
    tags: ['Design contemporain', 'Éco-construction', 'Intérieur', 'Commercial'],
    bio: 'Architecte passionnée par le design durable et l\'architecture bioclimatique. Expérience en projets résidentiels et commerciaux dans toute la région.',
    calendarSeed: 7,
    stats: [
    { icon: 'Leaf', value: 'Éco-certifiée', label: 'Construction verte' },
    { icon: 'Award', value: '4.6/5', label: 'Note moyenne' },
    { icon: 'Clock', value: '< 4h', label: 'Temps de réponse' },
    { icon: 'MapPin', value: 'Plateau', label: 'Zone principale' }],

    portfolio: [
    { icon: 'Leaf', title: 'Maison bioclimatique Bingerville', description: 'Conception éco-responsable, panneaux solaires, récupération eau', value: '120 000 000 FCFA', date: 'Avr 2026' },
    { icon: 'Store', title: 'Showroom automobile Yopougon', description: 'Espace commercial 800m², façade vitrée contemporaine', value: '95 000 000 FCFA', date: 'Fév 2026' }],

    reviews: [
    { author: 'Serge Konan', rating: 5, date: 'Avr 2026', text: 'Ama a une vision unique et moderne. Notre maison est magnifique et très économe en énergie.' },
    { author: 'Isabelle Ouédraogo', rating: 4, date: 'Mar 2026', text: 'Très bonne architecte, créative et sérieuse. Délais légèrement dépassés mais qualité au rendez-vous.' },
    { author: 'Patrick N\'Guessan', rating: 5, date: 'Jan 2026', text: 'Professionnelle et talentueuse. Je referai appel à elle sans hésiter.' }]

  }],

  electricien: [
  {
    id: 'elec-1', name: 'Brice Yao', title: 'Électricien certifié', verified: true,
    color: 'from-yellow-500 to-amber-500', rating: 4.9, completedJobs: 128, experience: 15,
    responseTime: '< 1h', priceRange: 'À partir de 40 000 FCFA',
    tags: ['Tableau électrique', 'Domotique', 'Climatisation', 'Urgences'],
    bio: 'Électricien certifié avec 15 ans d\'expérience. Intervention rapide sur Abidjan et environs. Spécialiste en installations résidentielles et domotique.',
    calendarSeed: 2,
    stats: [
    { icon: 'Zap', value: '128 missions', label: 'Réalisées' },
    { icon: 'Award', value: '4.9/5', label: 'Note moyenne' },
    { icon: 'Clock', value: '< 1h', label: 'Temps de réponse' },
    { icon: 'Shield', value: 'Certifié', label: 'Norme NFC 15-100' }],

    portfolio: [
    { icon: 'Zap', title: 'Installation complète villa Riviera', description: 'Tableau 63A, 24 circuits, domotique Legrand', value: '850 000 FCFA', date: 'Mai 2026' },
    { icon: 'Cpu', title: 'Domotique immeuble Marcory', description: 'Automatisation éclairage, volets, alarme pour 8 appartements', value: '2 400 000 FCFA', date: 'Mar 2026' },
    { icon: 'AlertTriangle', title: 'Mise aux normes Plateau', description: 'Remplacement tableau vétuste, mise en conformité', value: '320 000 FCFA', date: 'Fév 2026' }],

    reviews: [
    { author: 'Christiane Aka', rating: 5, date: 'Mai 2026', text: 'Brice est intervenu en urgence un dimanche soir. Très réactif, travail propre et soigné. Tarif raisonnable. Je le recommande à tous mes voisins !' },
    { author: 'Didier Koné', rating: 5, date: 'Avr 2026', text: 'Excellent électricien, très professionnel et ponctuel.' },
    { author: 'Sophie Touré', rating: 5, date: 'Mar 2026', text: 'Installation domotique parfaite, tout fonctionne à merveille.' },
    { author: 'Marc Coulibaly', rating: 4, date: 'Jan 2026', text: 'Bon travail, quelques finitions à revoir mais globalement satisfait.' }]

  },
  {
    id: 'elec-2', name: 'Adjoua Koffi', title: 'Technicienne électricité', verified: false,
    color: 'from-orange-400 to-yellow-500', rating: 4.4, completedJobs: 62, experience: 6,
    responseTime: '< 3h', priceRange: 'À partir de 35 000 FCFA',
    tags: ['Résidentiel', 'Dépannage', 'Éclairage LED', 'Prises'],
    bio: 'Technicienne spécialisée en électricité résidentielle. Interventions soignées et tarifs compétitifs. Disponible en semaine et le samedi.',
    calendarSeed: 9,
    stats: [
    { icon: 'Zap', value: '62 missions', label: 'Réalisées' },
    { icon: 'Award', value: '4.4/5', label: 'Note moyenne' },
    { icon: 'Clock', value: '< 3h', label: 'Temps de réponse' },
    { icon: 'MapPin', value: 'Yopougon', label: 'Zone principale' }],

    portfolio: [
    { icon: 'Lightbulb', title: 'Rénovation éclairage LED villa', description: 'Remplacement 45 points lumineux, économie 60% énergie', value: '280 000 FCFA', date: 'Avr 2026' },
    { icon: 'Plug', title: 'Ajout prises et circuits', description: 'Extension tableau, 8 nouvelles prises, 2 circuits dédiés', value: '195 000 FCFA', date: 'Mar 2026' }],

    reviews: [
    { author: 'Raoul Bédié', rating: 4, date: 'Avr 2026', text: 'Bonne technicienne, travail propre et tarifs corrects.' },
    { author: 'Nathalie Goba', rating: 5, date: 'Mar 2026', text: 'Très professionnelle et ponctuelle. Je recommande.' },
    { author: 'Théodore Assi', rating: 4, date: 'Fév 2026', text: 'Satisfait du travail effectué, délai respecté.' }]

  }],

  plombier: [
  {
    id: 'plomb-1', name: 'Séraphin Gnagne', title: 'Maître plombier', verified: true,
    color: 'from-sky-500 to-blue-500', rating: 4.7, completedJobs: 95, experience: 18,
    responseTime: '< 2h', priceRange: 'À partir de 35 000 FCFA',
    tags: ['Sanitaire', 'Chauffe-eau', 'Urgences', 'Rénovation'],
    bio: 'Maître plombier avec 18 ans d\'expérience. Intervention d\'urgence 7j/7. Spécialiste en plomberie sanitaire, chauffe-eau solaire et réparations.',
    calendarSeed: 5,
    stats: [
    { icon: 'Droplets', value: '95 missions', label: 'Réalisées' },
    { icon: 'Award', value: '4.7/5', label: 'Note moyenne' },
    { icon: 'Clock', value: '7j/7', label: 'Disponibilité' },
    { icon: 'Shield', value: 'Garanti', label: '1 an de garantie' }],

    portfolio: [
    { icon: 'Droplets', title: 'Plomberie complète villa neuve', description: 'Installation sanitaires 4 salles de bain, cuisine, buanderie', value: '1 200 000 FCFA', date: 'Avr 2026' },
    { icon: 'Sun', title: 'Chauffe-eau solaire 300L', description: 'Installation et raccordement chauffe-eau solaire thermique', value: '650 000 FCFA', date: 'Mar 2026' }],

    reviews: [
    { author: 'Cécile Amoussou', rating: 5, date: 'Mai 2026', text: 'Séraphin est intervenu rapidement pour une fuite importante. Travail impeccable et prix honnête.' },
    { author: 'Franck Dembélé', rating: 5, date: 'Avr 2026', text: 'Très bon plombier, sérieux et compétent.' },
    { author: 'Evelyne Kra', rating: 4, date: 'Mar 2026', text: 'Bon travail, quelques jours de délai mais résultat satisfaisant.' }]

  }],

  decorateur: [
  {
    id: 'deco-1', name: 'Laetitia Brou', title: 'Décoratrice d\'intérieur', verified: true,
    color: 'from-pink-500 to-rose-500', rating: 4.9, completedJobs: 54, experience: 9,
    responseTime: '< 3h', priceRange: 'À partir de 80 000 FCFA',
    tags: ['Contemporain', 'Africain moderne', 'Mobilier', 'Couleurs'],
    bio: 'Décoratrice passionnée alliant esthétique contemporaine et touches africaines. Chaque espace est une œuvre unique pensée pour vous.',
    calendarSeed: 4,
    stats: [
    { icon: 'Palette', value: '54 projets', label: 'Réalisés' },
    { icon: 'Award', value: '4.9/5', label: 'Note moyenne' },
    { icon: 'Clock', value: '< 3h', label: 'Temps de réponse' },
    { icon: 'Star', value: 'Top 5%', label: 'Décoratrices CI' }],

    portfolio: [
    { icon: 'Sofa', title: 'Salon contemporain Cocody', description: 'Réaménagement complet, mobilier sur mesure, éclairage d\'ambiance', value: '3 500 000 FCFA', date: 'Avr 2026' },
    { icon: 'Bed', title: 'Suite parentale Riviera', description: 'Dressing intégré, tête de lit capitonnée, papier peint texturé', value: '1 800 000 FCFA', date: 'Fév 2026' }],

    reviews: [
    { author: 'Audrey Konan', rating: 5, date: 'Mai 2026', text: 'Laetitia a transformé notre appartement en un vrai havre de paix. Son sens du détail et sa créativité sont exceptionnels. Résultat au-delà de nos espérances !' },
    { author: 'Bertrand Yao', rating: 5, date: 'Mar 2026', text: 'Magnifique travail, très à l\'écoute de nos envies.' },
    { author: 'Sandrine Aka', rating: 5, date: 'Jan 2026', text: 'Professionnelle et talentueuse. Notre maison est méconnaissable !' }]

  }],

  demenageur: [
  {
    id: 'dem-1', name: 'Transport Abidjan Express', title: 'Déménagement professionnel', verified: true,
    color: 'from-green-500 to-emerald-500', rating: 4.5, completedJobs: 210, experience: 10,
    responseTime: '< 2h', priceRange: 'À partir de 50 000 FCFA',
    tags: ['Emballage', 'Stockage', 'Grand volume', 'Fragiles'],
    bio: 'Entreprise de déménagement professionnelle avec flotte de véhicules adaptés. Emballage soigné, assurance incluse, disponible 7j/7.',
    calendarSeed: 1,
    stats: [
    { icon: 'Truck', value: '210 déménagements', label: 'Réalisés' },
    { icon: 'Award', value: '4.5/5', label: 'Note moyenne' },
    { icon: 'Shield', value: 'Assuré', label: 'Tous risques' },
    { icon: 'Package', value: '5 camions', label: 'Flotte' }],

    portfolio: [
    { icon: 'Truck', title: 'Déménagement villa 5 pièces', description: 'Emballage complet, transport, installation dans nouveau logement', value: '350 000 FCFA', date: 'Mai 2026' },
    { icon: 'Building2', title: 'Déménagement bureau entreprise', description: 'Mobilier de bureau, matériel informatique, archives', value: '580 000 FCFA', date: 'Avr 2026' }],

    reviews: [
    { author: 'Joëlle Assi', rating: 5, date: 'Mai 2026', text: 'Équipe très professionnelle, ponctuelle et soigneuse. Aucun objet abîmé. Je recommande vivement !' },
    { author: 'Hervé Coulibaly', rating: 4, date: 'Avr 2026', text: 'Bon service, quelques retards mais travail bien fait.' },
    { author: 'Mireille Touré', rating: 5, date: 'Mar 2026', text: 'Parfait du début à la fin. Merci à toute l\'équipe !' }]

  }],

  nettoyage: [
  {
    id: 'net-1', name: 'CleanPro Abidjan', title: 'Nettoyage professionnel', verified: true,
    color: 'from-teal-500 to-cyan-500', rating: 4.6, completedJobs: 183, experience: 7,
    responseTime: '< 2h', priceRange: 'À partir de 30 000 FCFA',
    tags: ['Fin de chantier', 'Entretien', 'Désinfection', 'Vitres'],
    bio: 'Entreprise de nettoyage certifiée avec équipes formées et matériel professionnel. Nettoyage fin de chantier, entretien régulier et désinfection.',
    calendarSeed: 6,
    stats: [
    { icon: 'Sparkles', value: '183 missions', label: 'Réalisées' },
    { icon: 'Award', value: '4.6/5', label: 'Note moyenne' },
    { icon: 'Users', value: '12 agents', label: 'Équipe formée' },
    { icon: 'Shield', value: 'Certifié', label: 'ISO 9001' }],

    portfolio: [
    { icon: 'Sparkles', title: 'Nettoyage fin de chantier villa', description: 'Déblaiement, nettoyage complet, vitrerie, sols', value: '180 000 FCFA', date: 'Mai 2026' },
    { icon: 'Building2', title: 'Entretien immeuble 20 appartements', description: 'Nettoyage parties communes, 3x/semaine', value: '450 000 FCFA/mois', date: 'Contrat en cours' }],

    reviews: [
    { author: 'Pauline Koffi', rating: 5, date: 'Mai 2026', text: 'Travail impeccable après notre chantier. Maison livrée parfaitement propre.' },
    { author: 'Gilles Bamba', rating: 4, date: 'Avr 2026', text: 'Bon service, équipe sérieuse et efficace.' },
    { author: 'Véronique Diallo', rating: 5, date: 'Mar 2026', text: 'Très satisfaite, je renouvelle le contrat chaque mois.' }]

  }],

  menuisier: [
  {
    id: 'men-1', name: 'Atelier Bois & Design', title: 'Menuiserie sur mesure', verified: true,
    color: 'from-orange-500 to-red-500', rating: 4.7, completedJobs: 76, experience: 14,
    responseTime: '< 4h', priceRange: 'Sur devis',
    tags: ['Sur mesure', 'Portes', 'Placards', 'Parquet'],
    bio: 'Atelier de menuiserie artisanale spécialisé dans les créations sur mesure. Bois massif, contreplaqué, MDF. Finitions soignées garanties.',
    calendarSeed: 8,
    stats: [
    { icon: 'Hammer', value: '76 projets', label: 'Réalisés' },
    { icon: 'Award', value: '4.7/5', label: 'Note moyenne' },
    { icon: 'Clock', value: '< 4h', label: 'Temps de réponse' },
    { icon: 'Leaf', value: 'Bois certifié', label: 'FSC' }],

    portfolio: [
    { icon: 'DoorOpen', title: 'Portes intérieures villa', description: '8 portes bois massif avec huisseries, finition laquée', value: '1 600 000 FCFA', date: 'Avr 2026' },
    { icon: 'LayoutGrid', title: 'Dressing chambre parentale', description: 'Dressing 4m linéaires, tiroirs, penderies, éclairage intégré', value: '950 000 FCFA', date: 'Mar 2026' }],

    reviews: [
    { author: 'Léon Assi', rating: 5, date: 'Avr 2026', text: 'Travail d\'artisan exceptionnel. Nos portes sont magnifiques et la finition est parfaite.' },
    { author: 'Hortense Koné', rating: 5, date: 'Mar 2026', text: 'Dressing superbe, exactement ce que nous voulions.' },
    { author: 'Armand Touré', rating: 4, date: 'Fév 2026', text: 'Très bon menuisier, délais un peu longs mais qualité irréprochable.' }]

  }],

  climatisation: [
  {
    id: 'clim-1', name: 'FroidTech CI', title: 'Technicien climatisation', verified: true,
    color: 'from-indigo-500 to-blue-500', rating: 4.8, completedJobs: 142, experience: 11,
    responseTime: '< 2h', priceRange: 'À partir de 60 000 FCFA',
    tags: ['Installation', 'Entretien', 'Réparation', 'Multi-split'],
    bio: 'Technicien certifié en climatisation et froid. Installation, entretien et réparation de toutes marques. Devis gratuit sous 24h.',
    calendarSeed: 3,
    stats: [
    { icon: 'Wind', value: '142 missions', label: 'Réalisées' },
    { icon: 'Award', value: '4.8/5', label: 'Note moyenne' },
    { icon: 'Shield', value: 'Certifié', label: 'Toutes marques' },
    { icon: 'Clock', value: '< 2h', label: 'Temps de réponse' }],

    portfolio: [
    { icon: 'Wind', title: 'Installation multi-split villa', description: '5 unités intérieures, 2 groupes extérieurs Daikin', value: '2 800 000 FCFA', date: 'Mai 2026' },
    { icon: 'Wrench', title: 'Entretien annuel immeuble', description: 'Révision 24 climatiseurs, nettoyage filtres, recharge gaz', value: '960 000 FCFA', date: 'Avr 2026' }],

    reviews: [
    { author: 'Rosine Bédié', rating: 5, date: 'Mai 2026', text: 'Installation rapide et soignée. Technicien très compétent et de bon conseil.' },
    { author: 'Julien Konan', rating: 5, date: 'Avr 2026', text: 'Excellent service, climatisation qui fonctionne parfaitement.' },
    { author: 'Agnès Yao', rating: 4, date: 'Mar 2026', text: 'Bon technicien, tarifs corrects.' }]

  }],

  securite: [
  {
    id: 'sec-1', name: 'SecurAbidjan', title: 'Systèmes de sécurité', verified: true,
    color: 'from-slate-600 to-gray-700', rating: 4.6, completedJobs: 89, experience: 13,
    responseTime: '< 3h', priceRange: 'Sur devis',
    tags: ['Alarme', 'Vidéosurveillance', 'Contrôle accès', 'Gardiennage'],
    bio: 'Entreprise spécialisée en sécurité électronique et humaine. Installation d\'alarmes, caméras IP, contrôle d\'accès et télésurveillance 24h/24.',
    calendarSeed: 2,
    stats: [
    { icon: 'ShieldCheck', value: '89 installations', label: 'Réalisées' },
    { icon: 'Award', value: '4.6/5', label: 'Note moyenne' },
    { icon: 'Clock', value: '24h/24', label: 'Télésurveillance' },
    { icon: 'Shield', value: 'Agréé', label: 'Ministère Intérieur' }],

    portfolio: [
    { icon: 'Camera', title: 'Vidéosurveillance villa 8 caméras', description: 'Caméras HD extérieures/intérieures, NVR 2To, accès smartphone', value: '1 850 000 FCFA', date: 'Avr 2026' },
    { icon: 'Lock', title: 'Contrôle accès immeuble', description: 'Interphone vidéo, badge RFID, portail automatique', value: '2 400 000 FCFA', date: 'Mar 2026' }],

    reviews: [
    { author: 'Norbert Assi', rating: 5, date: 'Avr 2026', text: 'Installation professionnelle, équipe sérieuse. Système de surveillance très performant.' },
    { author: 'Clarisse Bamba', rating: 4, date: 'Mar 2026', text: 'Bon service, quelques ajustements post-installation mais réactifs.' },
    { author: 'Edmond Koné', rating: 5, date: 'Fév 2026', text: 'Très satisfait, je me sens beaucoup plus en sécurité.' }]

  }],

  geometre: [
  {
    id: 'geo-1', name: 'Cabinet Topogéo CI', title: 'Géomètre-Expert', verified: true,
    color: 'from-blue-500 to-cyan-500', rating: 4.7, completedJobs: 38, experience: 16,
    responseTime: '< 4h', priceRange: 'À partir de 150 000 FCFA',
    tags: ['Bornage', 'Topographie', 'Division', 'Cadastre'],
    bio: 'Cabinet de géomètres-experts agréé. Bornage, relevés topographiques, divisions parcellaires et documents cadastraux officiels.',
    calendarSeed: 6,
    stats: [
    { icon: 'Ruler', value: '38 missions', label: 'Réalisées' },
    { icon: 'Award', value: '4.7/5', label: 'Note moyenne' },
    { icon: 'Shield', value: 'Agréé', label: 'Ordre des Géomètres' },
    { icon: 'FileText', value: 'Officiel', label: 'Documents légaux' }],

    portfolio: [
    { icon: 'Map', title: 'Bornage parcelle Bingerville', description: 'Délimitation officielle 2500m², pose bornes, procès-verbal', value: '350 000 FCFA', date: 'Avr 2026' },
    { icon: 'Ruler', title: 'Division parcellaire Yopougon', description: 'Division en 4 lots, plans cadastraux, dossier complet', value: '680 000 FCFA', date: 'Mar 2026' }],

    reviews: [
    { author: 'Théophile Kra', rating: 5, date: 'Avr 2026', text: 'Cabinet très sérieux, documents livrés dans les délais. Travail précis et professionnel.' },
    { author: 'Marguerite Diallo', rating: 4, date: 'Mar 2026', text: 'Bon géomètre, dossier bien constitué.' },
    { author: 'Sylvain Konan', rating: 5, date: 'Jan 2026', text: 'Très satisfait, je recommande ce cabinet.' }]

  }]

};

const STEPS = ['Service', 'Professionnel', 'Détails', 'Confirmation'];

const SORT_OPTIONS = [
{ value: 'rating', label: 'Meilleures notes' },
{ value: 'jobs', label: 'Plus expérimentés' },
{ value: 'response', label: 'Réponse rapide' }];


const ProfessionalServicesBooking = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const stateData = location?.state || {};

  const [step, setStep] = useState(1);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [bookingRef, setBookingRef] = useState(null);
  const [error, setError] = useState(null);
  const [myBookings, setMyBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [showMyBookings, setShowMyBookings] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);

  // DB professionals state
  const [dbProfessionals, setDbProfessionals] = useState([]);
  const [loadingProfessionals, setLoadingProfessionals] = useState(false);

  // Professional search/filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('rating');
  const [filterVerified, setFilterVerified] = useState(false);

  const [form, setForm] = useState({
    transactionType: stateData?.transactionType || 'achat',
    propertyId: stateData?.propertyId || '',
    propertyAddress: stateData?.propertyAddress || '',
    clientName: user?.user_metadata?.full_name || '',
    clientEmail: user?.email || '',
    clientPhone: '',
    preferredDate: '',
    preferredTime: '',
    address: stateData?.propertyAddress || '',
    description: '',
    budgetEstimate: '',
    notes: ''
  });

  useEffect(() => {
    if (user) {
      setForm((prev) => ({
        ...prev,
        clientName: prev?.clientName || user?.user_metadata?.full_name || '',
        clientEmail: prev?.clientEmail || user?.email || ''
      }));
    }
  }, [user]);

  const loadMyBookings = async () => {
    if (!user) return;
    setLoadingBookings(true);
    const { data } = await professionalBookingService?.getUserBookings();
    setMyBookings(data || []);
    setLoadingBookings(false);
  };

  const handleServiceSelect = (service) => {
    setSelectedService(service);
    setSelectedProfessional(null);
    setDbProfessionals([]);
    setSearchQuery('');
    setSortBy('rating');
    setFilterVerified(false);
    setStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Fetch approved professionals from DB when a service is selected
  useEffect(() => {
    if (!selectedService) return;
    const fetchApproved = async () => {
      setLoadingProfessionals(true);
      const { data } = await getApprovedProfessionals({ category: selectedService?.id });
      setDbProfessionals(
        (data || [])?.map((p) => normalizeDbProfessional(p, selectedService?.color))
      );
      setLoadingProfessionals(false);
    };
    fetchApproved();
  }, [selectedService]);

  const handleProfessionalSelect = (professional) => {
    setSelectedProfessional(professional);
  };

  const handleProfessionalContinue = () => {
    if (!selectedProfessional) return;
    setStep(3);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFormChange = (e) => {
    const { name, value } = e?.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSubmitting(true);
    setError(null);

    const { data, error: bookingError } = await professionalBookingService?.createBooking({
      serviceType: selectedService?.id,
      transactionType: form?.transactionType,
      propertyId: form?.propertyId || null,
      clientName: form?.clientName,
      clientEmail: form?.clientEmail,
      clientPhone: form?.clientPhone,
      preferredDate: form?.preferredDate,
      preferredTime: form?.preferredTime,
      address: form?.address,
      description: form?.description,
      budgetEstimate: form?.budgetEstimate,
      notes: form?.notes
    });

    setSubmitting(false);

    if (bookingError) {
      setError(bookingError);
      return;
    }

    setBookingRef(data?.id);
    setSubmitted(true);
    setStep(4);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleReset = () => {
    setStep(1);
    setSelectedService(null);
    setSelectedProfessional(null);
    setSubmitted(false);
    setBookingRef(null);
    setError(null);
    setSearchQuery('');
    setForm((prev) => ({
      ...prev,
      preferredDate: '',
      preferredTime: '',
      description: '',
      budgetEstimate: '',
      notes: ''
    }));
  };

  const handleCancelBooking = async (bookingId) => {
    await professionalBookingService?.cancelBooking(bookingId);
    loadMyBookings();
  };

  // Filtered & sorted professionals — approved from DB only
  const professionals = useMemo(() => {
    let filtered = (dbProfessionals || [])?.filter((p) => {
      const q = searchQuery?.toLowerCase();
      const matchesSearch =
      !q ||
      p?.name?.toLowerCase()?.includes(q) ||
      p?.title?.toLowerCase()?.includes(q) ||
      p?.tags?.some((t) => t?.toLowerCase()?.includes(q));
      const matchesVerified = !filterVerified || p?.verified;
      return matchesSearch && matchesVerified;
    });
    filtered = [...filtered]?.sort((a, b) => {
      if (sortBy === 'rating') return (b?.rating || 0) - (a?.rating || 0);
      if (sortBy === 'jobs') return (b?.completedJobs || 0) - (a?.completedJobs || 0);
      if (sortBy === 'response') return (a?.responseTimeHours || 99) - (b?.responseTimeHours || 99);
      return 0;
    });
    return filtered;
  }, [dbProfessionals, searchQuery, sortBy, filterVerified]);

  const statusLabel = {
    pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-700' },
    confirmed: { label: 'Confirmé', color: 'bg-blue-100 text-blue-700' },
    in_progress: { label: 'En cours', color: 'bg-indigo-100 text-indigo-700' },
    completed: { label: 'Terminé', color: 'bg-green-100 text-green-700' },
    cancelled: { label: 'Annulé', color: 'bg-red-100 text-red-700' }
  };

  const serviceById = (id) => SERVICES?.find((s) => s?.id === id);
  const today = new Date()?.toISOString()?.split('T')?.[0];

  return (
    <>
      <Helmet>
        <title>Réserver un Professionnel | Kapital States</title>
        <meta name="description" content="Réservez directement un professionnel après votre achat ou location : architecte, électricien, plombier, déménageur et plus encore." />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50/30">
        <Header />

        <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">

          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center gap-2 text-sm text-text-secondary mb-3">
              <Link to="/homepage" className="hover:text-brand-earth transition-colors">Accueil</Link>
              <Icon name="ChevronRight" size={14} />
              <span className="text-text-primary font-medium">Services Professionnels</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-display font-display-semibold text-brand-earth leading-tight">
                  Réserver un Professionnel
                </h1>
                <p className="text-text-secondary mt-1 text-sm sm:text-base">
                  Après votre achat ou location, faites appel à nos partenaires certifiés
                </p>
              </div>
              {user &&
              <button
                onClick={() => {setShowMyBookings(!showMyBookings);if (!showMyBookings) loadMyBookings();}}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border bg-white hover:bg-surface text-sm font-medium text-text-primary transition-all shadow-ios-light">

                  <Icon name="ClipboardList" size={16} />
                  Mes réservations
                </button>
              }
            </div>
          </div>

          {/* My Bookings Panel */}
          {showMyBookings && user &&
          <div className="mb-8 bg-white rounded-2xl border border-border shadow-ios-light overflow-hidden">
              <div className="px-5 py-4 border-b border-border/50 bg-surface/50 flex items-center justify-between">
                <h2 className="font-display font-display-semibold text-brand-earth text-base">Mes Réservations</h2>
                <button onClick={() => setShowMyBookings(false)} className="p-1.5 rounded-lg hover:bg-surface transition-colors">
                  <Icon name="X" size={16} />
                </button>
              </div>
              <div className="p-5">
                {loadingBookings ?
              <div className="flex items-center justify-center py-8 gap-3 text-text-secondary">
                    <div className="w-5 h-5 border-2 border-brand-gold border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm">Chargement...</span>
                  </div> :
              myBookings?.length === 0 ?
              <div className="text-center py-8 text-text-secondary">
                    <Icon name="CalendarX" size={32} className="mx-auto mb-2 opacity-40" />
                    <p className="text-sm">Aucune réservation pour le moment</p>
                  </div> :

              <div className="space-y-3">
                    {myBookings?.map((booking) => {
                  const svc = serviceById(booking?.service_type);
                  const st = statusLabel?.[booking?.booking_status] || statusLabel?.pending;
                  return (
                    <div key={booking?.id} className="flex items-center justify-between p-4 rounded-xl border border-border/60 bg-surface/30 hover:bg-surface/60 transition-colors">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${svc?.color || 'from-gray-400 to-gray-500'} flex items-center justify-center flex-shrink-0`}>
                              <Icon name={svc?.icon || 'Briefcase'} size={16} color="white" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-sm text-text-primary truncate">{svc?.label || booking?.service_type}</p>
                              <p className="text-xs text-text-secondary">{booking?.preferred_date} {booking?.preferred_time && `· ${booking?.preferred_time}`}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${st?.color}`}>{st?.label}</span>
                            {booking?.booking_status === 'pending' &&
                        <button
                          onClick={() => handleCancelBooking(booking?.id)}
                          className="text-xs text-red-500 hover:text-red-700 transition-colors px-2 py-1 rounded-lg hover:bg-red-50">

                                Annuler
                              </button>
                        }
                          </div>
                        </div>);

                })}
                  </div>
              }
              </div>
            </div>
          }

          {/* Step Indicator */}
          {!submitted &&
          <div className="flex items-center justify-center mb-8 gap-0 border-none">
              {STEPS?.map((s, i) => {
              const idx = i + 1;
              const isActive = step === idx;
              const isDone = step > idx;
              return (
                <React.Fragment key={s}>
                  <div className="flex flex-col items-center">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300 ${
                  isDone ? 'bg-brand-earth text-white' : isActive ? 'bg-brand-gold text-white shadow-md' : 'bg-surface text-text-secondary border border-border'}`
                  }>
                      {isDone ? <Icon name="Check" size={16} /> : idx}
                    </div>
                    <span className={`text-xs mt-1.5 font-medium ${isActive ? 'text-brand-earth' : 'text-text-secondary'}`}>{s}</span>
                  </div>
                  {i < STEPS?.length - 1 &&
                <div className={`h-0.5 w-12 sm:w-20 mx-1 mb-5 transition-all duration-300 ${step > idx ? 'bg-brand-earth' : 'bg-border'}`} />
                }
                </React.Fragment>
              );

            })}
            </div>
          }

          {/* STEP 1: Service Selection */}
          {step === 1 &&
          <div>
              <div className="mb-6 p-4 bg-white rounded-2xl border-border shadow-ios-light border-none border-0">
                <p className="text-sm font-medium text-text-secondary mb-3">Type de transaction</p>
                <div className="flex gap-3 border-none">
                  {['achat', 'location']?.map((type) =>
                <button
                  key={type}
                  onClick={() => setForm((prev) => ({ ...prev, transactionType: type }))}
                  className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-semibold border-none border-0 transition-all duration-200 ${
                  form?.transactionType === type ?
                  'bg-brand-earth text-white border-brand-earth shadow-ios-light' :
                  'bg-surface text-text-secondary border-border hover:border-brand-earth/50'}`
                  }>

                      {type === 'achat' ? '🏠 Achat' : '🔑 Location'}
                    </button>
                )}
                </div>
              </div>

              <p className="text-sm text-text-secondary mb-5 text-center">
                Sélectionnez le type de professionnel dont vous avez besoin
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {SERVICES?.map((service) =>
              <button
                key={service?.id}
                onClick={() => handleServiceSelect(service)}
                className={`group relative flex flex-col items-center gap-3 p-4 rounded-2xl hover:shadow-ios-medium hover:-translate-y-0.5 transition-all duration-200 text-center`}>

                    <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br from-gray-900 to-black flex items-center justify-center shadow-ios-light group-hover:scale-110 transition-transform duration-200`}>
                      <Icon name={service?.icon} size={22} color="white" strokeWidth={2} />
                    </div>
                    <div>
                      <p className="font-semibold text-sm text-text-primary leading-tight">{service?.label}</p>
                      <p className="text-xs text-text-secondary mt-0.5 leading-tight hidden sm:block">{service?.priceRange}</p>
                    </div>
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-5 h-5 bg-brand-earth rounded-full flex items-center justify-center">
                        <Icon name="ArrowRight" size={10} color="white" />
                      </div>
                    </div>
                  </button>
              )}
              </div>
            </div>
          }

          {/* STEP 2: Professional Selection */}
          {step === 2 && selectedService &&
          <div>
              {/* Service context banner */}
              <div className={`mb-6 p-4 rounded-2xl text-white flex items-center justify-between`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <Icon name={selectedService?.icon} size={20} color="white" />
                  </div>
                  <div>
                    <p className="font-display font-display-semibold text-base">{selectedService?.label}</p>
                    <p className="text-white/80 text-xs">{selectedService?.description}</p>
                  </div>
                </div>
                <button
                onClick={() => setStep(1)}
                className="text-white/80 hover:text-white text-xs flex items-center gap-1 bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg transition-colors">

                  <Icon name="ArrowLeft" size={12} />
                  Changer
                </button>
              </div>

              {/* Search & Filters */}
              <div className="bg-white rounded-2xl border-border shadow-ios-light p-4 mb-6 border-none border-0">
                <div className="flex flex-col sm:flex-row gap-3">
                  {/* Search */}
                  <div className="flex-1 relative">
                    <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary/60" />
                    <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    placeholder="Rechercher par nom, spécialité..."
                    className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-border bg-surface/50 text-sm text-text-primary placeholder:text-text-secondary/60 focus:outline-none focus:ring-2 focus:ring-brand-gold/40 focus:border-brand-gold transition-all" />

                  </div>
                  {/* Sort */}
                  <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e?.target?.value)}
                  className="px-4 py-2.5 rounded-xl border border-border bg-surface/50 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-gold/40 focus:border-brand-gold transition-all">

                    {SORT_OPTIONS?.map((opt) =>
                  <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
                  )}
                  </select>
                  {/* Verified filter */}
                  <button
                  onClick={() => setFilterVerified(!filterVerified)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                  filterVerified ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-border text-text-secondary hover:border-brand-earth/40'}`
                  }>

                    <Icon name="BadgeCheck" size={15} />
                    Vérifiés
                  </button>
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/40 border-none">
                  <p className="text-xs text-text-secondary">
                    <span className="font-semibold text-text-primary">{professionals?.length}</span> professionnel{professionals?.length !== 1 ? 's' : ''} disponible{professionals?.length !== 1 ? 's' : ''}
                  </p>
                  {selectedProfessional &&
                <div className="flex items-center gap-2 text-xs text-brand-earth font-medium">
                      <Icon name="CheckCircle2" size={13} />
                      {selectedProfessional?.name} sélectionné
                    </div>
                }
                </div>
              </div>

              {/* Professional Cards Grid */}
              {loadingProfessionals ?
            <div className="text-center py-16 bg-white rounded-2xl shadow-ios-light">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-earth mx-auto mb-3"></div>
                  <p className="text-text-secondary text-sm">Chargement des professionnels approuvés...</p>
                </div> :
            professionals?.length === 0 ?
            <div className="text-center py-16 bg-white rounded-2xl shadow-ios-light border-none">
                  <Icon name="UserX" size={40} className="mx-auto mb-3 text-text-secondary/40" />
                  <p className="text-text-secondary text-sm">Aucun professionnel approuvé disponible pour cette catégorie</p>
                  <button onClick={() => {setSearchQuery('');setFilterVerified(false);}} className="mt-3 text-xs text-brand-earth hover:underline">
                    Réinitialiser les filtres
                  </button>
                </div> :

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
                  {professionals?.map((pro) =>
              <ProfessionalProfileCard
                key={pro?.id}
                professional={pro}
                onSelect={handleProfessionalSelect}
                isSelected={selectedProfessional?.id === pro?.id} />

              )}
                </div>
            }

              {/* Continue button */}
              <div className="flex gap-3 sticky bottom-4">
                <button
                onClick={() => setStep(1)}
                className="flex items-center gap-2 px-5 py-3 rounded-xl border border-border bg-white text-sm font-medium text-text-secondary hover:bg-surface transition-all shadow-ios-light">

                  <Icon name="ArrowLeft" size={16} />
                  Retour
                </button>
                <button
                onClick={handleProfessionalContinue}
                disabled={!selectedProfessional}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-brand-earth text-white text-sm font-semibold hover:bg-brand-earth/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-ios-light">

                  <Icon name="ArrowRight" size={16} />
                  {selectedProfessional ? `Continuer avec ${selectedProfessional?.name}` : 'Sélectionnez un professionnel'}
                </button>
              </div>
            </div>
          }

          {/* STEP 3: Booking Form */}
          {step === 3 && selectedService && selectedProfessional &&
          <div className="bg-white rounded-2xl shadow-ios-light overflow-hidden">
              {/* Service + Professional header */}
              <div className={`px-6 py-5 bg-gradient-to-r ${selectedService?.color} text-white`}>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
                    <Icon name={selectedService?.icon} size={24} color="white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl font-display font-display-semibold">{selectedService?.label}</h2>
                    <p className="text-white/80 text-sm mt-0.5">{selectedService?.description}</p>
                  </div>
                </div>
                {/* Selected professional chip */}
                <div className="mt-4 flex items-center gap-2 bg-white/15 rounded-xl px-3 py-2 w-fit">
                  <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${selectedProfessional?.color} flex items-center justify-center flex-shrink-0`}>
                    <span className="text-white text-xs font-bold">{selectedProfessional?.name?.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="text-white text-xs font-semibold">{selectedProfessional?.name}</p>
                    <p className="text-white/70 text-xs">⭐ {selectedProfessional?.rating?.toFixed(1)} · {selectedProfessional?.completedJobs} missions</p>
                  </div>
                  <button
                  onClick={() => setStep(2)}
                  className="ml-2 text-white/70 hover:text-white text-xs underline">

                    Changer
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {/* Transaction type */}
                <div>
                  <label className="block text-sm font-semibold text-text-primary mb-2">Type de transaction *</label>
                  <div className="flex gap-3 border-none">
                    {['achat', 'location']?.map((type) =>
                  <button
                    key={type}
                    type="button"
                    onClick={() => setForm((prev) => ({ ...prev, transactionType: type }))}
                    className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-semibold border-none border-0 transition-all ${
                    form?.transactionType === type ?
                    'bg-brand-earth text-white border-brand-earth' : 'bg-surface text-text-secondary border-border hover:border-brand-earth/50'}`
                    }>

                        {type === 'achat' ? '🏠 Achat' : '🔑 Location'}
                      </button>
                  )}
                  </div>
                </div>

                {/* Client info */}
                <div>
                  <h3 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-3">Vos coordonnées</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1.5">Nom complet *</label>
                      <input
                      type="text"
                      name="clientName"
                      value={form?.clientName}
                      onChange={handleFormChange}
                      required
                      placeholder="Votre nom"
                      className="w-full px-4 py-2.5 rounded-xl border border-border bg-surface/50 text-sm text-text-primary placeholder:text-text-secondary/60 focus:outline-none focus:ring-2 focus:ring-brand-gold/40 focus:border-brand-gold transition-all" />

                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1.5">Email *</label>
                      <input
                      type="email"
                      name="clientEmail"
                      value={form?.clientEmail}
                      onChange={handleFormChange}
                      required
                      placeholder="votre@email.com"
                      className="w-full px-4 py-2.5 rounded-xl border border-border bg-surface/50 text-sm text-text-primary placeholder:text-text-secondary/60 focus:outline-none focus:ring-2 focus:ring-brand-gold/40 focus:border-brand-gold transition-all" />

                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1.5">Téléphone</label>
                      <input
                      type="tel"
                      name="clientPhone"
                      value={form?.clientPhone}
                      onChange={handleFormChange}
                      placeholder="+225 07 00 00 00 00"
                      className="w-full px-4 py-2.5 rounded-xl border border-border bg-surface/50 text-sm text-text-primary placeholder:text-text-secondary/60 focus:outline-none focus:ring-2 focus:ring-brand-gold/40 focus:border-brand-gold transition-all" />

                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1.5">Adresse du bien</label>
                      <input
                      type="text"
                      name="address"
                      value={form?.address}
                      onChange={handleFormChange}
                      placeholder="Adresse du bien concerné"
                      className="w-full px-4 py-2.5 rounded-xl border border-border bg-surface/50 text-sm text-text-primary placeholder:text-text-secondary/60 focus:outline-none focus:ring-2 focus:ring-brand-gold/40 focus:border-brand-gold transition-all" />

                    </div>
                  </div>
                </div>

                {/* Date & Time */}
                <div>
                  <h3 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-3">Date souhaitée</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1.5">Date *</label>
                      <input
                      type="date"
                      name="preferredDate"
                      value={form?.preferredDate}
                      onChange={handleFormChange}
                      required
                      min={today}
                      className="w-full px-4 py-2.5 rounded-xl border border-border bg-surface/50 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-gold/40 focus:border-brand-gold transition-all" />

                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1.5">Heure préférée</label>
                      <select
                      name="preferredTime"
                      value={form?.preferredTime}
                      onChange={handleFormChange}
                      className="w-full px-4 py-2.5 rounded-xl border border-border bg-surface/50 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-gold/40 focus:border-brand-gold transition-all">

                        <option value="">Indifférent</option>
                        <option value="08:00">08h00</option>
                        <option value="09:00">09h00</option>
                        <option value="10:00">10h00</option>
                        <option value="11:00">11h00</option>
                        <option value="14:00">14h00</option>
                        <option value="15:00">15h00</option>
                        <option value="16:00">16h00</option>
                        <option value="17:00">17h00</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Description & Budget */}
                <div>
                  <h3 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-3">Détails de la mission</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1.5">Description des travaux / besoins</label>
                      <textarea
                      name="description"
                      value={form?.description}
                      onChange={handleFormChange}
                      rows={3}
                      placeholder={`Décrivez vos besoins en ${selectedService?.label?.toLowerCase()}...`}
                      className="w-full px-4 py-2.5 rounded-xl border border-border bg-surface/50 text-sm text-text-primary placeholder:text-text-secondary/60 focus:outline-none focus:ring-2 focus:ring-brand-gold/40 focus:border-brand-gold transition-all resize-none" />

                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1.5">Budget estimé</label>
                      <input
                      type="text"
                      name="budgetEstimate"
                      value={form?.budgetEstimate}
                      onChange={handleFormChange}
                      placeholder="Ex: 200 000 FCFA"
                      className="w-full px-4 py-2.5 rounded-xl border border-border bg-surface/50 text-sm text-text-primary placeholder:text-text-secondary/60 focus:outline-none focus:ring-2 focus:ring-brand-gold/40 focus:border-brand-gold transition-all" />

                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1.5">Notes complémentaires</label>
                      <textarea
                      name="notes"
                      value={form?.notes}
                      onChange={handleFormChange}
                      rows={2}
                      placeholder="Informations supplémentaires..."
                      className="w-full px-4 py-2.5 rounded-xl border border-border bg-surface/50 text-sm text-text-primary placeholder:text-text-secondary/60 focus:outline-none focus:ring-2 focus:ring-brand-gold/40 focus:border-brand-gold transition-all resize-none" />

                    </div>
                  </div>
                </div>

                {error &&
              <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
                    <Icon name="AlertCircle" size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
              }

                <div className="flex gap-3 pt-2">
                  <button
                  type="button"
                  onClick={() => {setStep(2);setError(null);}}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-border text-sm font-medium text-text-secondary hover:bg-surface transition-all">

                    <Icon name="ArrowLeft" size={16} />
                    Retour
                  </button>
                  <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl bg-brand-earth text-white text-sm font-semibold hover:bg-brand-earth/90 disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-ios-light">

                    {submitting ?
                  <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Envoi en cours...
                      </> :

                  <>
                        <Icon name="Send" size={16} />
                        Confirmer la réservation
                      </>
                  }
                  </button>
                </div>
              </form>
            </div>
          }

          {/* STEP 4: Confirmation */}
          {step === 4 && submitted &&
          <div className="bg-white rounded-2xl shadow-ios-light overflow-hidden">
              {!showReviewForm ?
            <div className="px-6 py-10 text-center">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
                    <Icon name="CheckCircle2" size={40} className="text-green-500" />
                  </div>
                  <h2 className="text-2xl font-display font-display-semibold text-brand-earth mb-2">
                    Réservation confirmée !
                  </h2>
                  <p className="text-text-secondary text-sm mb-6 max-w-sm mx-auto">
                    Votre demande de <strong>{selectedService?.label}</strong> avec <strong>{selectedProfessional?.name}</strong> a bien été enregistrée. Vous serez contacté sous 24h.
                  </p>

                  {bookingRef &&
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-surface rounded-xl border border-border mb-6">
                      <Icon name="Hash" size={14} className="text-text-secondary" />
                      <span className="text-xs text-text-secondary">Référence :</span>
                      <span className="text-xs font-mono font-semibold text-brand-earth">{bookingRef?.slice(0, 8)?.toUpperCase()}</span>
                    </div>
              }

                  {/* Summary card */}
                  <div className="bg-surface/50 rounded-xl border border-border/60 p-4 text-left mb-6 max-w-sm mx-auto">
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${selectedService?.color} flex items-center justify-center`}>
                        <Icon name={selectedService?.icon} size={18} color="white" />
                      </div>
                      <div>
                        <p className="font-semibold text-sm text-text-primary">{selectedService?.label}</p>
                        <p className="text-xs text-text-secondary capitalize">{form?.transactionType}</p>
                      </div>
                    </div>
                    {/* Professional info */}
                    <div className="flex items-center gap-2 mb-3 p-2 bg-white rounded-lg border border-border/50">
                      <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${selectedProfessional?.color} flex items-center justify-center flex-shrink-0`}>
                        <span className="text-white text-xs font-bold">{selectedProfessional?.name?.charAt(0)}</span>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-text-primary">{selectedProfessional?.name}</p>
                        <p className="text-xs text-text-secondary">⭐ {selectedProfessional?.rating?.toFixed(1)} · {selectedProfessional?.title}</p>
                      </div>
                    </div>
                    <div className="space-y-1.5 text-xs text-text-secondary">
                      <div className="flex items-center gap-2">
                        <Icon name="User" size={12} />
                        <span>{form?.clientName}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Icon name="Mail" size={12} />
                        <span>{form?.clientEmail}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Icon name="Calendar" size={12} />
                        <span>{form?.preferredDate}{form?.preferredTime && ` à ${form?.preferredTime}`}</span>
                      </div>
                      {form?.address &&
                  <div className="flex items-center gap-2">
                          <Icon name="MapPin" size={12} />
                          <span className="truncate">{form?.address}</span>
                        </div>
                  }
                    </div>
                  </div>

                  {/* Review CTA */}
                  {!reviewSubmitted &&
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 max-w-sm mx-auto text-left">
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Icon name="Star" size={18} className="text-amber-500" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-text-primary">Évaluez votre expérience</p>
                          <p className="text-xs text-text-secondary mt-0.5">
                            Partagez votre avis sur {selectedProfessional?.name} et aidez d'autres clients à choisir.
                          </p>
                          <button
                      onClick={() => setShowReviewForm(true)}
                      className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-amber-600 hover:text-amber-700 transition-colors">

                            <Icon name="PenLine" size={13} />
                            Laisser un avis
                          </button>
                        </div>
                      </div>
                    </div>
              }

                  {reviewSubmitted &&
              <div className="flex items-center gap-2 justify-center mb-6 text-green-600">
                      <Icon name="CheckCircle2" size={16} />
                      <span className="text-sm font-medium">Avis publié avec succès !</span>
                    </div>
              }

                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <button
                  onClick={handleReset}
                  className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border border-border text-sm font-medium text-text-secondary hover:bg-surface transition-all">

                      <Icon name="Plus" size={16} />
                      Nouvelle réservation
                    </button>
                    <Link
                  to="/homepage"
                  className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-brand-earth text-white text-sm font-semibold hover:bg-brand-earth/90 transition-all shadow-ios-light">

                      <Icon name="Home" size={16} />
                      Retour à l'accueil
                    </Link>
                  </div>
                </div> :

            <div className="p-6">
                  <PostBookingReviewForm
                booking={{
                  id: bookingRef,
                  clientName: form?.clientName,
                  clientEmail: form?.clientEmail
                }}
                professional={selectedProfessional}
                service={selectedService}
                onClose={() => setShowReviewForm(false)}
                onSubmitted={() => {
                  setShowReviewForm(false);
                  setReviewSubmitted(true);
                }} />

                </div>
            }
            </div>
          }
        </main>
      </div>
    </>
  );

};

export default ProfessionalServicesBooking;